import React, { useEffect, useState } from "react";
import { Subheading } from "../../../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../design/table";
import { Badge } from "../../../../design/badge";
import { Link } from "../../../../design/link";
import { useAttorney } from "../../../../contexts/AttorneyContext";
import { useCase } from "../../../../contexts/CaseContext";
import {
  CaseAttorney,
  ATTORNEY_ROLE_LABELS,
  getAttorneyDisplayName,
} from "@visitproof/types";
import AssignAttorneyButton from "./AssignAttorneyButton";
import EditAttorneyAssignmentButton from "./EditAttorneyAssignmentButton";
import RemoveAttorneyButton from "./RemoveAttorneyButton";
import { formatDateUTC } from "../../../../utils/date";

interface Props {
  caseId: string;
}

const CaseDetailAttorneyList: React.FC<Props> = ({ caseId }) => {
  const { attorneys, getCaseAttorneys } = useAttorney();
  const { parents, childList } = useCase();
  const [caseAttorneys, setCaseAttorneys] = useState<CaseAttorney[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCaseAttorneys = async () => {
      try {
        const assignments = await getCaseAttorneys(caseId);
        setCaseAttorneys(assignments.filter((a) => a.isActive));
      } catch (error) {
        console.error("Error loading case attorneys:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCaseAttorneys();
  }, [caseId, getCaseAttorneys]);

  const getAttorneyDetails = (attorneyId: string) => {
    return attorneys.find((a) => a.id === attorneyId);
  };

  const getRepresentedPartyName = (assignment: CaseAttorney): string | null => {
    if (assignment.representedParentId) {
      const parent = parents.find((p) => p.id === assignment.representedParentId);
      if (parent) {
        return `${parent.firstName} ${parent.lastName}`;
      }
    }
    if (assignment.representedChildIds && assignment.representedChildIds.length > 0) {
      const childNames = assignment.representedChildIds
        .map((childId) => {
          const child = childList.find((c) => c.id === childId);
          return child ? `${child.firstName} ${child.lastName}` : null;
        })
        .filter(Boolean);
      if (childNames.length > 0) {
        return childNames.join(", ");
      }
    }
    return null;
  };

  const handleAssignmentRemoved = (assignmentId: string) => {
    setCaseAttorneys((prev) => prev.filter((a) => a.id !== assignmentId));
  };

  const handleAssignmentAdded = (newAssignment: CaseAttorney) => {
    setCaseAttorneys((prev) => {
      // Avoid duplicates - check if this assignment already exists
      if (prev.some((a) => a.id === newAssignment.id)) {
        return prev;
      }
      return [...prev, newAssignment];
    });
  };

  const handleAssignmentUpdated = (updatedAssignment: CaseAttorney) => {
    setCaseAttorneys((prev) =>
      prev.map((a) => (a.id === updatedAssignment.id ? updatedAssignment : a))
    );
  };

  if (loading) {
    return (
      <>
        <div className="flex flex-wrap items-center justify-between w-full">
          <Subheading>Attorneys</Subheading>
        </div>
        <div className="py-8 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">Loading...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between w-full">
        <Subheading>Attorneys</Subheading>
        <div className="flex gap-4">
          <AssignAttorneyButton
            caseId={caseId}
            existingAssignments={caseAttorneys}
            onAssigned={handleAssignmentAdded}
          />
        </div>
      </div>

      {caseAttorneys.length === 0 && (
        <div className="py-8 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            No attorneys have been assigned to this case yet.
          </p>
        </div>
      )}

      {caseAttorneys.length > 0 && (
        <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Role</TableHeader>
              <TableHeader>Represents</TableHeader>
              <TableHeader>Dates</TableHeader>
              <TableHeader>Firm</TableHeader>
              <TableHeader>Contact</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {caseAttorneys.map((assignment) => {
              const attorney = getAttorneyDetails(assignment.attorneyId);
              if (!attorney) return null;

              const representedParty = getRepresentedPartyName(assignment);
              const roleLabel =
                assignment.role === "other" && assignment.roleLabel
                  ? assignment.roleLabel
                  : ATTORNEY_ROLE_LABELS[assignment.role];

              return (
                <TableRow key={assignment.id}>
                  <TableCell className="font-medium">
                    <Link
                      href={`/attorney/${attorney.id}`}
                      className="text-blue-600 hover:text-blue-500 dark:text-blue-400"
                    >
                      {getAttorneyDisplayName(attorney)}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge
                      color={
                        assignment.role === "gal" || assignment.role === "minors_counsel"
                          ? "purple"
                          : assignment.role === "casa"
                            ? "green"
                            : "blue"
                      }
                    >
                      {roleLabel}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {representedParty || (
                      <span className="text-zinc-400 dark:text-zinc-500">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-zinc-500 dark:text-zinc-400">
                    {assignment.startDate ? (
                      <div className="text-sm">
                        <span>{formatDateUTC(assignment.startDate)}</span>
                        {assignment.endDate ? (
                          <span> – {formatDateUTC(assignment.endDate)}</span>
                        ) : (
                          <span className="text-green-600 dark:text-green-400"> – Present</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-zinc-400 dark:text-zinc-500">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-zinc-500 dark:text-zinc-400">
                    {attorney.firmName || "—"}
                  </TableCell>
                  <TableCell>
                    {attorney.phoneNumber || attorney.email || "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <EditAttorneyAssignmentButton
                        assignment={assignment}
                        onUpdated={handleAssignmentUpdated}
                      />
                      <RemoveAttorneyButton
                        assignment={assignment}
                        attorneyName={getAttorneyDisplayName(attorney)}
                        onRemoved={() => handleAssignmentRemoved(assignment.id)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default CaseDetailAttorneyList;
